// fetchWithTimeout / fetchWithRetry — a `fetch` that cannot hang forever, and a
// bounded retry around it.
//
// WHY (2026-09-19, v1.184.2): the portal's first call, GET /api/me, was a raw
// `fetch` with no timeout. On 2026-09-18/19 the API host's managed-identity
// sidecar stalled on fresh workers and every /api/me that landed on one waited
// 62–394 s for an answer — the browser showed a bare spinner the whole time,
// and the user's natural response (reload) queued MORE requests behind the same
// stalled worker. An infinite spinner is indistinguishable from "down".
//
// This module turns that into: wait a bounded time, tell the user we're
// retrying, retry (a fresh request may be routed to a healthy instance), and
// after the last attempt fail loudly so the caller can render an honest
// "can't reach the service" screen instead of spinning.
//
// Dependency-free on purpose — useAuth.js imports it and client.js imports
// useAuth.js, so anything imported here must not import either of those.
//
// Retry policy: ONLY a rejected fetch is retried (timeout or network failure).
// Any HTTP response — 200, 401, 500, anything — is returned to the caller on
// the first attempt; HTTP status semantics belong to the caller (authGate.js
// classifies them). Retrying a 500 would re-run a server-side action.
//
// Tests: frontend/scripts/fetchWithTimeout.test.mjs

export class FetchTimeoutError extends Error {
  constructor(url, timeoutMs) {
    super(`Request to ${url} timed out after ${timeoutMs} ms`);
    this.name = 'FetchTimeoutError';
    this.url = url;
    this.timeoutMs = timeoutMs;
  }
}

/**
 * fetch with a hard timeout. Aborts the request via AbortController and rejects
 * with FetchTimeoutError when `timeoutMs` elapses without a response.
 *
 * NOTE: sets `signal` on the request; a caller-supplied `options.signal` is not
 * combined (nothing in the portal passes one). Add signal-chaining if that changes.
 *
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {{ timeoutMs?: number, fetchImpl?: typeof fetch }} [opts]
 * @returns {Promise<Response>}
 */
export async function fetchWithTimeout(url, options = {}, { timeoutMs = 20000, fetchImpl } = {}) {
  const doFetch = fetchImpl || globalThis.fetch;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await doFetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    // Our own abort is a timeout; anything else (network/CORS/DNS) passes through.
    if (controller.signal.aborted) throw new FetchTimeoutError(url, timeoutMs);
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/** Classify a fetch rejection for the retry callback / logs. */
export function fetchFailureReason(err) {
  if (err instanceof FetchTimeoutError) return 'timeout';
  return 'network';
}

/**
 * fetchWithTimeout, retried on REJECTION only (timeout / network), up to
 * `attempts` total tries. Before each retry, `onRetry` receives
 * `{ attempt, attempts, nextAttempt, reason, error }` so the UI can say what
 * is happening instead of spinning silently.
 *
 * Any HTTP response is returned immediately without retry (see module header).
 * After the last failed attempt the last error is thrown with `.attempts` set.
 *
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {{
 *   timeoutMs?: number,
 *   attempts?: number,
 *   delayMs?: number,
 *   onRetry?: (info: { attempt: number, attempts: number, nextAttempt: number, reason: 'timeout'|'network', error: Error }) => void,
 *   fetchImpl?: typeof fetch,
 *   sleep?: (ms: number) => Promise<void>,
 * }} [opts]
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(
  url,
  options = {},
  { timeoutMs = 20000, attempts = 3, delayMs = 0, onRetry, fetchImpl, sleep } = {},
) {
  const total = Math.max(1, Math.floor(attempts));
  const wait = sleep || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  let lastErr = null;
  for (let attempt = 1; attempt <= total; attempt++) {
    try {
      return await fetchWithTimeout(url, options, { timeoutMs, fetchImpl });
    } catch (err) {
      lastErr = err;
      if (attempt >= total) break;
      if (typeof onRetry === 'function') {
        try {
          onRetry({ attempt, attempts: total, nextAttempt: attempt + 1, reason: fetchFailureReason(err), error: err });
        } catch {
          // a UI callback must never break the retry loop
        }
      }
      if (delayMs > 0) await wait(delayMs);
    }
  }
  if (lastErr && typeof lastErr === 'object') lastErr.attempts = total;
  throw lastErr;
}
